import React, { useState, useEffect, useCallback } from "react";
import PostCard from "./PostCard";
import { toast } from "react-toastify";

const SocialFeed = ({ campaignId, currentUser, refreshTrigger }) => {
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [page, setPage] = useState(0);
  const [error, setError] = useState(null);

  const POSTS_PER_PAGE = 10;

  // Fetch posts
  const fetchPosts = useCallback(
    async (pageNumber = 0, append = false) => {
      try {
        if (pageNumber === 0) {
          setIsLoading(true);
          setError(null);
        } else {
          setIsLoadingMore(true);
        }

        // Prepare headers with auth token if available
        let headers = {};
        if (currentUser) {
          try {
            const { supabase } = await import("../../lib/supabase");
            const {
              data: { session },
            } = await supabase.auth.getSession();
            const token =
              session?.access_token || localStorage.getItem("access_token");
            if (token) {
              headers.Authorization = `Bearer ${token}`;
            }
          } catch (authError) {
            console.warn("Auth error while fetching posts:", authError);
          }
        }

        // Backend expects page index in `offset`
        const response = await fetch(
          `${process.env.NEXT_PUBLIC_BASE_URL}/api/gallery/campaign-posts/${campaignId}?limit=${POSTS_PER_PAGE}&offset=${pageNumber}`,
          { headers }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (result.success && result.posts) {
          const newPosts = result.posts;

          if (append && pageNumber > 0) {
            setPosts((prev) => {
              // Avoid duplicates
              const existingIds = new Set(prev.map((post) => post._id));
              const uniqueNewPosts = newPosts.filter(
                (post) => !existingIds.has(post._id)
              );
              return [...prev, ...uniqueNewPosts];
            });
          } else {
            setPosts(newPosts);
          }

          // Determine if more posts are available
          if (typeof result.postsCount === "number") {
            const loadedCount = (pageNumber + 1) * POSTS_PER_PAGE;
            setHasMore(loadedCount < result.postsCount);
          } else {
            setHasMore(newPosts.length === POSTS_PER_PAGE);
          }
          setPage(pageNumber);
        } else {
          if (pageNumber === 0) {
            setPosts([]);
          }
          setHasMore(false);
        }
      } catch (error) {
        console.error("Error fetching posts:", error);
        setError(error.message);

        if (pageNumber === 0) {
          setPosts([]);
          toast.error("Failed to load posts. Please try again.");
        } else {
          toast.error("Failed to load more posts.");
        }
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [campaignId]
  );

  // Initial load and refresh
  useEffect(() => {
    fetchPosts(0, false);
  }, [fetchPosts, refreshTrigger]);

  // Load more
  const loadMorePosts = () => {
    if (!isLoadingMore && hasMore) {
      fetchPosts(page + 1, true);
    }
  };

  // Handle post updates
  const handlePostUpdate = useCallback(() => {
    // no-op to avoid extra fetches
  }, []);

  // Retry
  const handleRetry = () => {
    fetchPosts(0, false);
  };

  if (isLoading && posts.length === 0) {
    return (
      <div className="space-y-6">
        {/* Loading skeletons */}
        {[...Array(3)].map((_, index) => (
          <div
            key={index}
            className="bg-white dark:bg-jacarta-800 rounded-xl border border-jacarta-100 dark:border-jacarta-600 p-4 animate-pulse"
          >
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-jacarta-200 dark:bg-jacarta-700 rounded-full"></div>
              <div className="flex-1">
                <div className="w-32 h-4 bg-jacarta-200 dark:bg-jacarta-700 rounded mb-2"></div>
                <div className="w-20 h-3 bg-jacarta-200 dark:bg-jacarta-700 rounded"></div>
              </div>
            </div>
            <div className="w-full h-4 bg-jacarta-200 dark:bg-jacarta-700 rounded mb-2"></div>
            <div className="w-3/4 h-4 bg-jacarta-200 dark:bg-jacarta-700 rounded mb-4"></div>
            <div className="w-full h-48 bg-jacarta-200 dark:bg-jacarta-700 rounded mb-4"></div>
            <div className="flex space-x-4">
              <div className="w-16 h-8 bg-jacarta-200 dark:bg-jacarta-700 rounded"></div>
              <div className="w-20 h-8 bg-jacarta-200 dark:bg-jacarta-700 rounded"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error && posts.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="mb-4">
          <svg
            className="mx-auto h-16 w-16 text-jacarta-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        </div>
        <h3 className="text-lg font-display font-semibold text-jacarta-700 dark:text-white mb-2">
          Failed to Load Posts
        </h3>
        <p className="text-jacarta-500 dark:text-jacarta-400 mb-4">
          We couldn't load the campaign posts. Please check your connection and
          try again.
        </p>
        <button
          onClick={handleRetry}
          className="px-6 py-2 bg-accent text-white rounded-2lg hover:bg-accent-dark transition-colors font-display font-semibold"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="mb-4">
          <svg
            className="mx-auto h-16 w-16 text-jacarta-400"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M22 7.42993V13.4299C22 14.9299 21.5 16.1799 20.62 17.0599C19.75 17.9299 18.5 18.4299 17 18.4299V20.5599C17 21.3599 16.11 21.84 15.45 21.4L11 18.4299H8.88C8.96 18.1299 9 17.8199 9 17.4999C9 16.4799 8.61 15.54 7.97 14.83C7.25 14.01 6.18 13.4999 5 13.4999C3.88 13.4999 2.86 13.96 2.13 14.71C2.04 14.31 2 13.8799 2 13.4299V7.42993C2 4.42993 4 2.42993 7 2.42993H17C20 2.42993 22 4.42993 22 7.42993Z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeMiterlimit="10"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M9 17.5C9 17.82 8.96 18.13 8.88 18.43C8.79 18.83 8.63001 19.22 8.42001 19.56C7.73001 20.72 6.46 21.5 5 21.5C3.97 21.5 3.04 21.11 2.34 20.47C2.04 20.21 1.77999 19.9 1.57999 19.56C1.20999 18.96 1 18.25 1 17.5C1 16.42 1.43 15.43 2.13 14.71C2.86 13.96 3.88 13.5 5 13.5C6.18 13.5 7.25 14.01 7.97 14.83C8.61 15.54 9 16.48 9 17.5Z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeMiterlimit="10"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M6.06995 18.5399L3.94995 16.4299"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeMiterlimit="10"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M6.04993 16.46L3.92993 18.5699"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeMiterlimit="10"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M8.5 10.5H15.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeMiterlimit="10"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <h3 className="text-lg font-display font-semibold text-jacarta-700 dark:text-white mb-2">
          No Posts Yet
        </h3>
        <p className="text-jacarta-500 dark:text-jacarta-400 mb-4">
          {currentUser
            ? "Be the first to share something about this campaign!"
            : "No one has shared anything about this campaign yet."}
        </p>
        {!currentUser && (
          <p className="text-sm text-jacarta-400">
            Sign in to share your thoughts and photos.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Posts */}
      {posts.map((post) => (
        <PostCard
          key={post._id}
          post={post}
          currentUser={currentUser}
          onPostUpdate={handlePostUpdate}
        />
      ))}

      {/* Load More */}
      {hasMore && (
        <div className="text-center py-6">
          <button
            onClick={loadMorePosts}
            disabled={isLoadingMore}
            className="px-6 py-3 bg-jacarta-100 dark:bg-jacarta-700 text-jacarta-700 dark:text-white rounded-xl hover:bg-jacarta-200 dark:hover:bg-jacarta-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-display font-semibold"
          >
            {isLoadingMore ? (
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-accent"></div>
                <span>Loading more posts...</span>
              </div>
            ) : (
              "Load More Posts"
            )}
          </button>
        </div>
      )}

      {/* End of feed */}
      {!hasMore && posts.length > 0 && (
        <div className="text-center py-6">
          <p className="text-sm text-jacarta-500 dark:text-jacarta-400">
            You've reached the end of the feed
          </p>
        </div>
      )}

      {/* Loading more */}
      {isLoadingMore && (
        <div className="space-y-6">
          {[...Array(2)].map((_, index) => (
            <div
              key={index}
              className="bg-white dark:bg-jacarta-800 rounded-xl border border-jacarta-100 dark:border-jacarta-600 p-4 animate-pulse"
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-jacarta-200 dark:bg-jacarta-700 rounded-full"></div>
                <div className="flex-1">
                  <div className="w-32 h-4 bg-jacarta-200 dark:bg-jacarta-700 rounded mb-2"></div>
                  <div className="w-20 h-3 bg-jacarta-200 dark:bg-jacarta-700 rounded"></div>
                </div>
              </div>
              <div className="w-full h-4 bg-jacarta-200 dark:bg-jacarta-700 rounded mb-2"></div>
              <div className="w-3/4 h-4 bg-jacarta-200 dark:bg-jacarta-700 rounded mb-4"></div>
              <div className="w-full h-48 bg-jacarta-200 dark:bg-jacarta-700 rounded mb-4"></div>
              <div className="flex space-x-4">
                <div className="w-16 h-8 bg-jacarta-200 dark:bg-jacarta-700 rounded"></div>
                <div className="w-20 h-8 bg-jacarta-200 dark:bg-jacarta-700 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SocialFeed;
